import React, { useState, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Fade,
  LinearProgress,
  Divider,
  Avatar,
} from "@mui/material";
import {
  MyLocation as GpsIcon,
  Storefront as ShopIcon,
  AddLocationAlt as NewShopIcon,
  LocationCity as CityIcon,
} from "@mui/icons-material";

const containerStyle = {
  width: "100%",
  height: "250px",
  borderRadius: "16px",
};

const Location = () => {
  const API_KEY = "AIzaSyAawfaCQ57puQnjtjON7WzoxW9GUsT4oCs"; // Tvoj API ključ

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
  });

  const [step, setStep] = useState(1);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState("");
  const [coords, setCoords] = useState({ lat: 43.8563, lng: 18.4131 });
  const [addressInfo, setAddressInfo] = useState({
    city: "",
    municipality: "",
  });

  const [mockObjects] = useState([
    { id: 1, name: "Caffe Bar Central", address: "Glavna ulica 10" },
    { id: 2, name: "Hotel Park Lounge", address: "Šetalište 5" },
    { id: 3, name: "Premium Club", address: "Trg Slobode 2" },
  ]);

  // Funkcija za prepoznavanje grada i općine (BiH specifično)
  const fetchAddressDetails = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${API_KEY}`,
      );
      const data = await response.json();

      if (data.status === "OK") {
        let city = "";
        let municipality = "";

        // Prolazimo kroz komponente adrese kako bismo izvukli grad i općinu
        data.results[0].address_components.forEach((component) => {
          if (component.types.includes("locality")) {
            city = component.long_name;
          }
          if (component.types.includes("administrative_area_level_2")) {
            municipality = component.long_name; // U BiH administrative_area_level_2 je obično općina
          }
        });

        setAddressInfo({
          city: city || "Nepoznat grad",
          municipality: municipality || "Nepoznata općina",
        });
      }
    } catch (error) {
      console.error("Greška pri Reverse Geocodingu:", error);
    }
  };

  const startCalibration = () => {
    setStep(2);
    setLoadingProgress(0);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newLat = pos.coords.latitude;
          const newLng = pos.coords.longitude;
          setCoords({ lat: newLat, lng: newLng });

          // Čim dobijemo koordinate, tražimo adresu
          fetchAddressDetails(newLat, newLng);
        },
        (err) => console.error(err),
        { enableHighAccuracy: true },
      );
    }

    const duration = 10000;
    const interval = 100;
    let current = 0;

    const timer = setInterval(() => {
      current += interval;
      const progress = (current / duration) * 100;
      setLoadingProgress(progress);

      if (progress < 25) setLoadingText("Povezivanje sa satelitima...");
      else if (progress < 50)
        setLoadingText("Kalibracija preciznosti (G3-LBS)...");
      else if (progress < 75) setLoadingText("Prepoznavanje općine i grada...");
      else setLoadingText("Lokacija i administrativna zona potvrđeni!");

      if (current >= duration) {
        clearInterval(timer);
        setTimeout(() => setStep(3), 800);
      }
    }, interval);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: { xs: 1, md: 2 } }}>
      {step === 1 && (
        <Fade in>
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: "center",
              borderRadius: 4,
              border: "1px solid #e2e8f0",
              bgcolor: "white",
            }}
          >
            <GpsIcon sx={{ fontSize: 60, color: "#284c7a", mb: 2 }} />
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: "#1e293b", mb: 1 }}
            >
              Poveži objekat
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Pritisnite gumb za automatsko skeniranje lokacije i uparivanje s
              objektom.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={startCalibration}
              sx={{
                bgcolor: "#284c7a",
                py: 2,
                borderRadius: 3,
                fontWeight: 700,
                textTransform: "none",
                fontSize: "1.1rem",
              }}
            >
              Započni lociranje
            </Button>
          </Paper>
        </Fade>
      )}

      {step === 2 && (
        <Fade in>
          <Box sx={{ textAlign: "center", mt: 8 }}>
            <CircularProgress
              size={80}
              thickness={2}
              sx={{ mb: 4, color: "#284c7a" }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, mb: 1, color: "#1e293b" }}
            >
              {loadingText}
            </Typography>
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": { bgcolor: "#284c7a" },
                }}
              />
            </Box>
          </Box>
        </Fade>
      )}

      {step === 3 && (
        <Fade in>
          <Box>
            {/* Google Mapa s Prikazom Općine/Grada */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2, p: 1 }}
            >
              <Avatar sx={{ bgcolor: "#284c7a" }}>
                <CityIcon />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 900,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                  }}
                >
                  Pronađena lokacija
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}
                >
                  {addressInfo.city}, {addressInfo.municipality}
                </Typography>
              </Box>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                mb: 3,
              }}
            >
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={coords}
                  zoom={17}
                  options={{
                    disableDefaultUI: true,
                    zoomControl: false,
                    gestureHandling: "none",
                  }}
                >
                  <Marker position={coords} />
                </GoogleMap>
              ) : (
                <Box
                  sx={{
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "#284c7a",
                  color: "white",
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  PRECIZNOST: +/- 2 metra (Kalibrirano)
                </Typography>
              </Box>
            </Paper>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <ShopIcon sx={{ color: "#284c7a" }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: "#1e293b" }}
              >
                Objekti u blizini
              </Typography>
            </Stack>

            <List sx={{ mb: 2 }}>
              {mockObjects.map((obj) => (
                <Paper
                  key={obj.id}
                  elevation={0}
                  sx={{
                    mb: 1.2,
                    border: "1px solid #f1f5f9",
                    borderRadius: 3,
                    "&:hover": { border: "1px solid #284c7a" },
                  }}
                >
                  <ListItemButton sx={{ p: 2 }}>
                    <ListItemIcon>
                      <ShopIcon sx={{ color: "#284c7a" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={obj.name}
                      secondary={obj.address}
                      primaryTypographyProps={{
                        fontWeight: 800,
                        color: "#1e293b",
                      }}
                    />
                  </ListItemButton>
                </Paper>
              ))}
            </List>

            <Divider sx={{ my: 3 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 900, color: "#94a3b8" }}
              >
                ILI
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<NewShopIcon />}
              sx={{
                py: 2,
                borderRadius: 3,
                borderWidth: 2,
                fontWeight: 800,
                color: "#284c7a",
                borderColor: "#284c7a",
                textTransform: "none",
              }}
            >
              Registriraj novi objekt na ovoj lokaciji
            </Button>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default Location;
